/**
 * Full-screen modal preview of a template's units and criteria (dark-mode compatible).
 */
import React, { useEffect } from "react";
import { translateUi } from "../i18n/index";
import { ClipboardList, Clock, Lock, X } from "../icons";
import { GbrModalBackdrop } from "../GbrModalBackdrop";
import { cb, cbCn } from "./cycleBuilderTheme";
import { formatCycleDurationLabel } from "./formatCycleDuration";
import { templateDurationMonths, templateLibraryStats, TemplateUnitsCriteriaScroll } from "./TemplateSelectPanes";
import type { LibraryCycle } from "./cycleLibraryUi";

export function TemplateCriteriaPreviewModal({
  open,
  cycle,
  locked = false,
  isDarkMode = false,
  previewKind = "template",
  onClose,
}: {
  open: boolean;
  cycle: LibraryCycle | null;
  locked?: boolean;
  isDarkMode?: boolean;
  /** "template" = catalogue tile preview; "cycle" = saved company cycle. */
  previewKind?: "template" | "cycle";
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !cycle) return null;

  const stats = templateLibraryStats(cycle);
  const duration = formatCycleDurationLabel(templateDurationMonths(cycle));

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={cbCn(
          "relative flex max-h-[min(92vh,900px)] w-full flex-col overflow-hidden rounded-t-2xl border shadow-2xl sm:max-w-3xl sm:rounded-2xl",
          isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-preview-title"
      >
        <GbrModalBackdrop opacity={isDarkMode ? 0.06 : 0.09} />
        <header
          className={cbCn(
            "relative shrink-0 border-b px-4 py-4 sm:px-6",
            isDarkMode ? "border-slate-700 bg-slate-900/95" : "border-slate-100 bg-slate-50/95"
          )}
        >
          <div className="flex items-start gap-3 pr-10">
            <span
              className={cbCn(
                "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
                isDarkMode ? "bg-indigo-500/20 text-indigo-200" : "bg-indigo-100 text-indigo-700"
              )}
              aria-hidden
            >
              <ClipboardList className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className={cbCn("text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-indigo-300" : "text-indigo-700")}>
                {previewKind === "cycle" ? translateUi("Cycle criteria") : translateUi("Template preview")}
              </p>
              <h2
                id="template-preview-title"
                className={cbCn("mt-0.5 text-base font-semibold leading-snug break-words sm:text-lg", isDarkMode ? "text-slate-50" : "text-slate-900")}
              >
                {cycle.name}
              </h2>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <span
                  className={cbCn(
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                    isDarkMode ? "bg-slate-800 text-slate-300" : "bg-white text-slate-600 ring-1 ring-slate-200"
                  )}
                >
                  <Clock className="h-3 w-3" aria-hidden />
                  {duration}
                </span>
                <span className={cbCn("rounded-full px-2 py-0.5 text-[11px] font-medium", isDarkMode ? "bg-slate-800 text-slate-300" : "bg-white text-slate-600 ring-1 ring-slate-200")}>
                  {stats.units} {translateUi("units")}
                </span>
                <span className={cbCn("rounded-full px-2 py-0.5 text-[11px] font-medium", isDarkMode ? "bg-slate-800 text-slate-300" : "bg-white text-slate-600 ring-1 ring-slate-200")}>
                  {stats.criteria} {translateUi("criteria")}
                </span>
                {locked && (
                  <span className={cbCn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", isDarkMode ? "bg-slate-800 text-slate-200" : "bg-slate-200 text-slate-700")}>
                    <Lock className="h-3 w-3" aria-hidden />
                    {translateUi("Fixed criteria")}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cbCn(
              "absolute right-3 top-3 rounded-xl p-2 transition-colors",
              isDarkMode ? "text-slate-400 hover:bg-slate-800 hover:text-slate-100" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            )}
            aria-label={translateUi("Close")}
          >
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="relative min-h-0 flex-1 overflow-hidden">
          <TemplateUnitsCriteriaScroll
            cycle={cycle}
            isDarkMode={isDarkMode}
            className="max-h-[min(72vh,720px)]"
            collapsibleUnits
          />
        </div>
        <footer
          className={cbCn(
            "relative shrink-0 border-t px-4 py-3 sm:px-6",
            isDarkMode ? "border-slate-700 bg-slate-900/95" : "border-slate-100 bg-slate-50/95"
          )}
        >
          <p className={cbCn("text-xs", cb.bodySm(isDarkMode))}>
            {translateUi("Expand each unit to view its criteria. Green rows are Demonstrate; amber rows are Explain.")}
          </p>
        </footer>
      </div>
    </div>
  );
}
